import React, { useState, useEffect } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { EnumField } from "../ConfigFields/EnumField";
import typography from "../../../../cssUtils/typography";
import { useAuth } from "../../../../hooks/AuthProvider";
import { FORMS_FORMS, FORMS_FORM_EDIT } from "../../../../config/urls";
import { ColorsConstants } from "../../../../cssUtils/constants";
import { flattenElements } from "../../../Forms/Components/utils";

// Field types whose submitted value is worth mapping into a SurveyResponse
// column (static/display-only elements like TextHeading/Footer/Image carry
// no name). Matches LeadgenFormPreset's list.
const MAPPABLE_ELEMENT_TYPES = [
  "TextInput",
  "TextArea",
  "RadioButtonsGroup",
  "CheckboxGroup",
  "Dropdown",
  "OptIn",
];

// SurveyResponse's 5 fixed columns — "Other" is always its own radio option
// in this survey, never a follow-up free-text field, so there's no
// current_behaviour_other/resume_change_other target here.
const SURVEY_MAPPING_TARGETS = [
  { value: "", label: "Not mapped" },
  { value: "current_behaviour", label: "Current Trading Behaviour" },
  { value: "reduce_reason", label: "Reason For Reducing/Stopping" },
  { value: "specific_incident", label: "Specific Incident" },
  { value: "resume_change", label: "What Would Bring You Back" },
  { value: "satisfaction", label: "Overall Satisfaction" },
];

const getMappableFields = (formJson) => {
  const fields = [];
  (formJson?.screens || []).forEach((screen) => {
    const children = flattenElements(screen?.layout?.children?.[0]?.children);
    children.forEach((element, index) => {
      if (element?.name && MAPPABLE_ELEMENT_TYPES.includes(element.type)) {
        // The component's own `label` (e.g. "Select one:") is usually just
        // generic placeholder text shared by every question on a screen —
        // the actual question text lives in the preceding TextCaption/Heading
        // element, so prefer that for a label a human can actually tell apart.
        const precedingText = children
          .slice(0, index)
          .reverse()
          .find((el) =>
            ["TextCaption", "TextHeading", "TextSubheading"].includes(el.type),
          );

        fields.push({
          name: element.name,
          label: precedingText?.text || element.label || element.name,
          screenTitle: screen.title || screen.id,
        });
      }
    });
  });
  return fields;
};

const SurveyQuestionsPreset = ({ schema, config, handleConfigChange }) => {
  const { getValidToken } = useAuth();
  const tenant = localStorage.getItem("tenant");

  const [forms, setForms] = useState([]);
  const [formsLoading, setFormsLoading] = useState(true);
  const [formsError, setFormsError] = useState("");

  const [selectedFormDetail, setSelectedFormDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [fieldMapping, setFieldMapping] = useState({});
  const [mappingError, setMappingError] = useState("");

  useEffect(() => {
    const load = async () => {
      setFormsLoading(true);
      setFormsError("");
      try {
        const token = await getValidToken();
        const response = await fetch(
          `${FORMS_FORMS}?limit=999&status=PUBLISHED&category=SURVEY`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
              "X-Tenant-Id": tenant,
            },
          },
        );
        if (!response.ok) throw new Error("Failed to load forms");
        const data = await response.json();
        setForms(data.results || []);
      } catch (err) {
        setFormsError("Could not load forms. Please try again.");
      } finally {
        setFormsLoading(false);
      }
    };
    load();
  }, []);

  const flowId = config["flow_id"];

  useEffect(() => {
    if (!flowId) {
      setSelectedFormDetail(null);
      setFieldMapping({});
      return;
    }
    const loadDetail = async () => {
      setDetailLoading(true);
      setMappingError("");
      try {
        const token = await getValidToken();
        const response = await fetch(`${FORMS_FORMS}${flowId}/`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-Tenant-Id": tenant,
          },
        });
        if (!response.ok) throw new Error("Failed to load form details");
        const data = await response.json();
        setSelectedFormDetail(data);
        setFieldMapping(data?.field_mapping || {});
      } catch (err) {
        setMappingError("Could not load field mapping. Please try again.");
      } finally {
        setDetailLoading(false);
      }
    };
    loadDetail();
  }, [flowId]);

  const selectedForm = forms.find((f) => f.id === flowId) || null;

  const mappableFields = getMappableFields(selectedFormDetail?.form_json);

  const handleFieldMappingChange = async (fieldName, bbColumn) => {
    const nextMapping = { ...fieldMapping };
    if (bbColumn) {
      nextMapping[fieldName] = bbColumn;
    } else {
      delete nextMapping[fieldName];
    }

    const previousMapping = fieldMapping;
    setFieldMapping(nextMapping);
    setMappingError("");

    try {
      const token = await getValidToken();
      const response = await fetch(FORMS_FORM_EDIT(selectedFormDetail.id), {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": tenant,
        },
        body: JSON.stringify({
          ...selectedFormDetail,
          field_mapping: nextMapping,
        }),
      });
      if (!response.ok) throw new Error("Failed to save field mapping");
      const data = await response.json();
      setSelectedFormDetail(data);
    } catch (err) {
      setFieldMapping(previousMapping);
      setMappingError("Could not save field mapping. Please try again.");
    }
  };

  return (
    <Stack spacing={3}>
      {/* Flow selector */}
      <Autocomplete
        options={forms}
        value={selectedForm}
        loading={formsLoading}
        onChange={(_, newValue) =>
          handleConfigChange("flow_id", newValue?.id ?? null)
        }
        getOptionLabel={(option) => option?.name || ""}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        noOptionsText={formsLoading ? "Loading forms…" : "No forms found"}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select Survey Flow"
            variant="outlined"
            required
            error={!!formsError}
            helperText={formsError || undefined}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {formsLoading && (
                    <CircularProgress color="inherit" size={16} />
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            InputLabelProps={{ sx: typography.bodyLarge }}
            inputProps={{
              ...params.inputProps,
              style: typography.bodyLarge,
            }}
          />
        )}
        renderOption={(props, option) => (
          <Stack
            {...props}
            key={option.id}
            direction="column"
            gap={0.25}
            sx={{
              alignItems: "flex-start !important",
              textAlign: "left",
              width: "100%",
            }}
          >
            <Typography sx={{ ...typography.bodyLarge }}>
              {option.name}
            </Typography>
          </Stack>
        )}
      />

      <EnumField
        label="On Exit Early"
        schema={schema?.properties?.["on_exit_early"] || {}}
        value={config["on_exit_early"]}
        onChange={(val) => handleConfigChange("on_exit_early", val)}
      />

      {flowId && (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Stack>
              <Typography sx={{ ...typography.bodyLarge, fontWeight: 600 }}>
                Field Mapping
              </Typography>
              <Typography sx={{ ...typography.bodySmall, color: "#61748C" }}>
                Map each question to its SurveyResponse column so responses
                show up directly on the Survey's Responses tab. Unmapped
                fields are still saved in the raw response.
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            {mappingError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {mappingError}
              </Alert>
            )}
            {detailLoading ? (
              <Stack alignItems="center" sx={{ py: 2 }}>
                <CircularProgress size={20} />
              </Stack>
            ) : mappableFields.length === 0 ? (
              <Typography sx={{ ...typography.bodySmall, color: "#61748C" }}>
                Add input fields (Radio Buttons Group, etc.) to a screen to
                map them.
              </Typography>
            ) : (
              <Stack spacing={2}>
                {mappableFields.map((field) => (
                  <Stack
                    key={field.name}
                    direction="row"
                    spacing={2}
                    alignItems="center"
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={typography.bodyLarge}>
                        {field.label}
                      </Typography>
                    </Box>
                    <FormControl sx={{ minWidth: 220 }} size="small">
                      <Select
                        value={fieldMapping[field.name] || ""}
                        onChange={(e) =>
                          handleFieldMappingChange(field.name, e.target.value)
                        }
                      >
                        {SURVEY_MAPPING_TARGETS.map((target) => (
                          <MenuItem key={target.value} value={target.value}>
                            {target.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                ))}
              </Stack>
            )}
          </AccordionDetails>
        </Accordion>
      )}
    </Stack>
  );
};

export default SurveyQuestionsPreset;
